import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Spinner from "../app/shared/Spinner";
import { ROUTES } from "../utils/key_helper";
import { useProfileContext } from "./context/ProfileContext";
import { useConfigContext } from "./context/ConfigContext";
import { useLocation } from "react-router-dom";
// Lazy-loaded pages
const Login = lazy(() => import("./auth-pages/Login"));
const ForgotPassword = lazy(() => import("./auth-pages/ForgotPassword"));
const Registration = lazy(() => import("./auth-pages/Registration"));
const NotFound = lazy(() => import("./user-pages/NotFound"));
const Profile = lazy(() => import("./user-pages/profile/Profile"));
const Dashboard = lazy(() => import("./user-pages/dashboard/Dashboard"));
const Bridge = lazy(() => import("./user-pages/bridge/Bridge"));
const BridgeInfo = lazy(() => import("./user-pages/bridge/BridgeInfo"));
const TradingViewBridgeSyntaxGenerator = lazy(() => import("./user-pages/bridge/TradingViewBridgeSyntaxGenerator"));
const Strategy = lazy(() => import("./user-pages/strategy/Strategy"));
const StrategyDetails = lazy(() => import("./user-pages/strategy/StrategyDetails"));
const MyStrategy = lazy(() => import("./user-pages/my-strategy/MyStrategy"));
const Wallet = lazy(() => import("./user-pages/wallet/Wallet"));
const ActivityLogs = lazy(() => import("./user-pages/activity-logs/ActivityLogs"));
const OrderHistory = lazy(() => import("./user-pages/order-history/OrderHistory"));
const Referral = lazy(() => import("./user-pages/referral/Referral"));
const Account = lazy(() => import("./user-pages/account/Account"));
const AccountDetails = lazy(() => import('./user-pages/account/AccountInfo'));
const AppRoutes = () => {
	const location = useLocation();
	//  Always call hooks unconditionally
	const { configData } = useConfigContext() || {};
	const { profileData } = useProfileContext() || {};
	//  Now safely determine if it's a no-layout route
	const noLayoutRoutes = [ROUTES.LOGIN, ROUTES.NOT_FOUND, ROUTES.FORGOT_PASSWORD, ROUTES.REGISTRATION];
	const isNoLayout = noLayoutRoutes.includes(location.pathname);

	return (
		<Suspense fallback={<Spinner />}>
			<Routes>
				<Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} />} />
				<Route path="/" element={<Navigate to={ROUTES.LOGIN} />} />
				<Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
				<Route path={ROUTES.LOGIN} element={<Login />} />
				<Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
				<Route path={ROUTES.REGISTRATION} element={<Registration />} />
				{/* Protected / Authenticated routes */}
				{!isNoLayout && (
					<>
						<Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
						<Route path={ROUTES.ACCOUNT} element={<Account />} />
						<Route path={ROUTES.ACCOUNT_DETAILS()} element={<AccountDetails />} />

						<Route path={ROUTES.STRATEGY} element={<Strategy />} />
						<Route path={ROUTES.STRATEGY_DETAILS()} element={<StrategyDetails />} />
						<Route path={ROUTES.MY_STRATEGY} element={<MyStrategy />} />
						<Route path={ROUTES.MY_STRATEGY_DETAILS()} element={<StrategyDetails />} />
						{/* If custom_bridge_allow OR json_bridge_allow than access this route */}
						{(profileData?.custom_bridge_allow || profileData?.json_bridge_allow) && (
							<Route path={ROUTES.BRIDGE} element={<Bridge />} />
						)}
						{/* If json_bridge_allow than access this route */}
						{profileData?.json_bridge_allow && (
							<Route path={ROUTES.TRADING_VIEW_SYNTAX_GENERATOR()} element={<TradingViewBridgeSyntaxGenerator />} />
						)}
						{/* If custom_bridge_allow than access this route */}
						{profileData?.custom_bridge_allow && (
							<Route path={ROUTES.CUSTOM_BRIDGE_DETAILS()} element={<BridgeInfo />} />
						)}
						{/* If payment enabled than access wallet and referral route */}
						{configData?.payment_enabled && (
							<>
								<Route path={ROUTES.WALLET} element={<Wallet />} />
								<Route path={ROUTES.REFERRAL} element={<Referral />} />
							</>
						)}
						<Route path={ROUTES.ACTIVITY_LOGS} element={<ActivityLogs />} />
						<Route path={ROUTES.PROFILE} element={<Profile />} />
						<Route path={ROUTES.ORDER_HISTORY} element={<OrderHistory />} />
					</>
				)}
			</Routes>
		</Suspense>
	);
};

export default AppRoutes;
